import cn from 'classnames';
import type { FC } from 'react';
import React from 'react';

import Icon, { IconTypes } from 'components/basic/Icon';
import LearnMoreLink from 'components/basic/LearnMoreLink';
import RainbowHighlight from 'components/basic/RainbowHighlight';
import Typograf from 'components/basic/Typograf';
import { BodyL, BodyM, Factoid, H1, H2, H3 } from 'components/basic/Typography';
import Tag from 'components/pagesComponents/landing/ForResearches/components/MakeYourData/components/Tag';
import { typeformUrl } from 'components/pagesComponents/landing/ForResearches/constants';

import s from './MakeYourData.module.scss';

const MakeYourData: FC = () => {
  return (
    <Typograf>
      <RainbowHighlight
        className={cn(
          s.rainbow,
          'mt-sm-23 mt-xm-20 mt-lg-23 mt-xl-30 mb-sm-23 mb-xm-20 mb-lg-23 mb-xl-30',
        )}
        text="Make your data work for you"
        buttonText="Apply now"
        href={typeformUrl}
      />
      <H1 className={cn(s.h1, 'mb-sm-6 mb-xm-8 mb-lg-12 mb-xl-10')}>
        {/* @ts-ignore */}
        <a name="Privacy-and-safety">
          We work according to the latest data protection guidelines and respect
          privacy
        </a>
      </H1>
      <div className="grid">
        <div className="g-col-lg-5 g-col-md-10 g-col-sm-12 g-col-xm-5 g-start-lg-1 g-start-md-2 g-start-xm-1">
          <H2 className={cn(s.subheader, 'mb-sm-5 mb-xm-4 mb-xl-6')}>
            {`Trusted by researchers 
from top Institutions`}
          </H2>
          <div>
            <div className={s.checkedItem}>
              <Icon icon={IconTypes.checkCircleOutline} color="breakfast" />
              <H3>IRB Approved</H3>
            </div>
            <div className={s.checkedItem}>
              <Icon icon={IconTypes.checkCircleOutline} color="breakfast" />
              <H3>HIPAA Compliant</H3>
            </div>
            <div className={s.checkedItem}>
              <Icon icon={IconTypes.checkCircleOutline} color="breakfast" />
              <H3>Secure Platform</H3>
            </div>
          </div>

          <div className={cn(s.factoids, 'mb-sm-12 mb-xm-10 mb-lg-0 mb-xl-10')}>
            <div className={s.factoid} style={{ gridArea: 'a' }}>
              <Factoid className="mb-1">5 000+</Factoid>
              <H3>Participants</H3>
            </div>
            <div className={s.factoid} style={{ gridArea: 'b' }}>
              <Factoid className="mb-1">50+</Factoid>
              <H3>Researchers</H3>
            </div>

            <div className={s.factoid} style={{ gridArea: 'c' }}>
              <Factoid className="mb-1">20+</Factoid>
              <H3>Institutions</H3>
            </div>
          </div>
        </div>

        <div className="g-col-lg-1 g-col-sm-0" />
        <div className="g-col-lg-6 g-col-xm-6 g-col-md-12 g-col-sm-12">
          <H2 className="mb-4">Treating your information responsibly </H2>
          <BodyL className="mb-8">
            Welltory is a HIPAA and GDPR complacence app. This means that we
            keep all personal and individual data private. You have full control
            over the participants in your study and their data. Any outside data
            is not accessible.
          </BodyL>
          <div className="grid">
            <div className="g-col-lg-6 g-col-xm-6 g-col-sm-12">
              <H3 className="mb-sm-2 mb-xl-4">HIPAA</H3>
              <BodyM className="mb-2">
                Health Insurance Portability andAccountability Act of 1996
              </BodyM>
              <BodyM className={s.lightText}>
                HIPAA prohibits healthcare providers and healthcare businesses,
                called covered entities, from disclosing protected information
                to anyone other than a patient and the patient's authorized
                representatives without their consent.
              </BodyM>
            </div>
            <div className="g-col-lg-6 g-col-xm-6 g-col-sm-12">
              <H3 className="mb-sm-2 mb-xl-4">GDPR</H3>
              <BodyM className="mb-2">
                The General Data Protection Regulation
              </BodyM>
              <BodyM className={s.lightText}>
                GDPR is a regulation in EU law on data protection and privacy in
                the European Union (EU) and the European Economic Area (EEA).
              </BodyM>
            </div>
          </div>
          <LearnMoreLink
            className="mt-sm-6 mt-lg-20 mt-xl-22"
            header="Look at an example of raw data export"
            href="https://github.com/Welltory/hrv-covid19"
          />
        </div>
      </div>

      <H1 Tag="h2" className="mt-sm-23 mt-xm-20 mt-lg-10 mb-sm-6">
        {/* @ts-ignore */}
        <a name="Exploring-data-from-Welltory">
          What kind of data can I get from Welltory?
        </a>
      </H1>
      <BodyL className="mb-4">
        If your research participants are using the free version of Welltory,
        you can track the following basic metrics:
      </BodyL>
      <div className={s.tagsWrapper}>
        <Tag text="SDNN" measure="ms" color="brand" />
        <Tag text="Heart Rate" measure="bpm" color="red" />
        <Tag text="Coefficient of Variation" color="warm_2" />
        <Tag text="Stress" measure="%" color="warm_1" />
        <Tag text="Energy" measure="%" color="yellow" />
        <Tag text="Focus" color="awake" />
      </div>
    </Typograf>
  );
};

export default MakeYourData;
